import React from 'react';
import { motion } from 'framer-motion';
import { 
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  CogIcon,
  CloudArrowUpIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

export default function Features() {
  const features = [
    {
      name: 'Cross-Platform Compatibility',
      description: 'Access your business data seamlessly across Web, Windows, Android, and iOS platforms with synchronized real-time updates.',
      icon: DevicePhoneMobileIcon,
      color: 'from-blue-500 to-cyan-500',
      details: ['Web Dashboard', 'Windows Desktop App', 'iOS Mobile App', 'Android Mobile App']
    },
    {
      name: 'Enterprise Security',
      description: 'Bank-level security protocols with end-to-end encryption, secure authentication, and compliance with industry standards.',
      icon: ShieldCheckIcon,
      color: 'from-green-500 to-emerald-500',
      details: ['SSL/TLS Encryption', 'Two-Factor Authentication', 'Regular Security Audits', 'GDPR Compliance']
    },
    {
      name: 'Scalable Architecture',
      description: 'Built to grow with your business. Our cloud-based infrastructure scales automatically to handle increased demand.',
      icon: ArrowTrendingUpIcon,
      color: 'from-purple-500 to-pink-500',
      details: ['Auto-scaling Infrastructure', 'Load Balancing', 'High Availability', 'Global CDN']
    },
    {
      name: 'Easy Integration',
      description: 'Seamlessly integrate with your existing systems and third-party applications through our comprehensive API.',
      icon: CogIcon,
      color: 'from-orange-500 to-red-500',
      details: ['RESTful APIs', 'Webhook Support', 'Third-party Integrations', 'Custom Connectors']
    },
    {
      name: 'Cloud-Based Solution',
      description: 'No installation required. Access your data from anywhere with automatic backups and real-time synchronization.',
      icon: CloudArrowUpIcon,
      color: 'from-indigo-500 to-blue-500',
      details: ['Automatic Backups', 'Real-time Sync', 'Offline Mode', 'Data Recovery']
    },
    {
      name: 'Advanced Analytics',
      description: 'Gain valuable insights with comprehensive reporting, analytics dashboards, and performance metrics.',
      icon: ArrowTrendingUpIcon,
      color: 'from-teal-500 to-cyan-500',
      details: ['Custom Reports', 'Performance Metrics', 'Business Intelligence', 'Export Capabilities']
    }
  ];

  return (
    <section id="features" className="py-24 sm:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-600">Features & Benefits</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Built for Modern Businesses
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our platform combines cutting-edge technology with user-friendly design to deliver 
              powerful solutions that work seamlessly across all your devices.
            </p>
          </motion.div>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid max-w-xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-2 xl:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${feature.color} mb-6`}>
                  <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">
                  {feature.name}
                </h3>
                <p className="text-base leading-7 text-gray-600 mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                      <div className="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Platform Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24 sm:mt-32"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 sm:p-12">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-white mb-4">
                Available on All Your Devices
              </h3>
              <p className="text-primary-100 max-w-2xl mx-auto">
                Whether you're at your desk, on the go, or working remotely, 
                Autiin keeps you connected to your business data.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: GlobeAltIcon, name: 'Web', desc: 'Browser-based access' },
                { icon: ComputerDesktopIcon, name: 'Windows', desc: 'Desktop application' },
                { icon: DevicePhoneMobileIcon, name: 'iOS', desc: 'iPhone & iPad app' },
                { icon: DevicePhoneMobileIcon, name: 'Android', desc: 'Mobile application' }
              ].map((platform, index) => (
                <motion.div
                  key={platform.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center group"
                >
                  <div className="flex justify-center mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm group-hover:bg-white/30 transition-all duration-200">
                      <platform.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{platform.name}</h4>
                  <p className="text-sm text-primary-100">{platform.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Security & Compliance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-24 sm:mt-32"
        >
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-12">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500">
                  <LockClosedIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Security & Compliance
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Your data security is our top priority. We implement industry-leading 
                security measures to protect your business information.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: 'Data Encryption', desc: 'End-to-end encryption for all data in transit and at rest' },
                { title: 'Regular Audits', desc: 'Third-party security audits and penetration testing' },
                { title: 'Compliance Ready', desc: 'GDPR, SOC 2, and industry-specific compliance standards' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
} 